import { ThunkAction, UnknownAction } from "@reduxjs/toolkit"
import { api, TAGS } from "@/store/api"
import { battleSocket } from "@/store/apis/battleAPI/battle.socket"
import {
  IBattleLegalActions,
  IBattleView,
  IGetLegalActionsArgs,
  IOpenBattleResponse,
  ISetBattleAutoArgs,
  IStartBattleRequest,
  ISubmitBattleActionArgs
} from "@/store/apis/battleAPI/battle.types"
import {
  applyAutoToggled,
  applyBattleFinished,
  applyRoundResolved
} from "@/store/apis/battleAPI/battleEvents"

export const battleAPI = api
  .enhanceEndpoints({ addTagTypes: [TAGS.BATTLE] })
  .injectEndpoints({
    endpoints: (build) => {
      const writeView = async (
        dispatch: (action: unknown) => unknown,
        queryFulfilled: PromiseLike<{ data: IBattleView }>
      ): Promise<void> => {
        try {
          const { data: view } = await queryFulfilled
          dispatch(battleAPI.util.upsertQueryData("getBattle", view.id, view))
          dispatch(
            battleAPI.util.upsertQueryData("getOpenBattle", undefined, {
              battle: view
            })
          )
        } catch {
          return
        }
      }

      return {
        getOpenBattle: build.query<IOpenBattleResponse, void>({
          query: () => ({ url: "/battles/open", method: "GET" }),
          providesTags: [TAGS.BATTLE]
        }),
        getBattle: build.query<IBattleView, string>({
          query: (id) => ({ url: `/battles/${id}`, method: "GET" }),
          providesTags: [TAGS.BATTLE],
          keepUnusedDataFor: 0,
          async onCacheEntryAdded(
            battleId,
            { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved }
          ) {
            try {
              await cacheDataLoaded
            } catch {
              return
            }
            const reread = (): void => {
              void dispatch(rereadBattle(battleId))
            }
            const stopReconnect = battleSocket.start(reread)
            battleSocket.addSub()
            const unbind = battleSocket.bindBattleEvents(battleId, {
              onRoundResolved: (data) => {
                updateCachedData((draft) => applyRoundResolved(draft, data))
                reread()
              },
              onAutoToggled: (data) =>
                updateCachedData((draft) => applyAutoToggled(draft, data)),
              onFinished: (data) =>
                updateCachedData((draft) => applyBattleFinished(draft, data))
            })
            try {
              await cacheEntryRemoved
            } finally {
              unbind()
              stopReconnect()
              battleSocket.removeSub()
            }
          }
        }),
        getLegalActions: build.query<IBattleLegalActions, IGetLegalActionsArgs>(
          {
            query: ({ battleId }) => ({
              url: `/battles/${battleId}/legal-actions`,
              method: "GET"
            })
          }
        ),
        startBattle: build.mutation<IBattleView, IStartBattleRequest>({
          query: (data) => ({ url: "/battles", method: "POST", data }),
          onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
            writeView(dispatch, queryFulfilled)
        }),
        submitAction: build.mutation<IBattleView, ISubmitBattleActionArgs>({
          query: ({ battleId, round, action }) => ({
            url: `/battles/${battleId}/rounds/${round}/action`,
            method: "POST",
            data: action
          }),
          onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
            writeView(dispatch, queryFulfilled)
        }),
        setAuto: build.mutation<IBattleView, ISetBattleAutoArgs>({
          query: ({ battleId, enabled }) => ({
            url: `/battles/${battleId}/auto`,
            method: "POST",
            data: { enabled }
          }),
          onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
            writeView(dispatch, queryFulfilled)
        }),
        flee: build.mutation<IBattleView, string>({
          query: (battleId) => ({
            url: `/battles/${battleId}/flee`,
            method: "POST"
          }),
          onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
            writeView(dispatch, queryFulfilled)
        })
      }
    }
  })

export const {
  useGetOpenBattleQuery,
  useGetBattleQuery,
  useGetLegalActionsQuery,
  useStartBattleMutation,
  useSubmitActionMutation,
  useSetAutoMutation,
  useFleeMutation
} = battleAPI

export const rereadBattle =
  (battleId: string): ThunkAction<unknown, unknown, unknown, UnknownAction> =>
  (dispatch) =>
    dispatch(
      battleAPI.endpoints.getBattle.initiate(battleId, {
        subscribe: false,
        forceRefetch: true
      })
    )
